import { InputTooltip } from '@components';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Field, type FieldAttributes } from 'formik';
import type { ReactElement, ReactNode } from 'react';

export type InputOption = {
  /** The display label in the dropdown */
  label: string;
  /** The value that formik will use when setting values */
  value: any;
};

type InputDropdownProps = {
  label?: string;
  name: string;
  options: InputOption[];
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** Optional function to do something after select */
  onSelect?: (arg0: any) => Promise<void>;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  sx?: SxProps<Theme>;
  renderValue?: (value: any) => ReactNode;
  readOnly?: boolean;
  displayEmpty?: boolean;
  selectSx?: SxProps<Theme>;
};

/**
 * A Slabstack formik-friendly dropdown.
 *
 * Must be used inside `<Formik><Form>{component}</Form></Formik>`.
 *
 * @example
 * <Formik {your_values_here} >
 * {(formikBag): ReactElement => (
 *   <Form>
 *     <InputDropdown
 *       label='Company name'
 *       name='company.id'
 *       options={companyOpts}
 *     />
 *   </Form>
 * </Formik>
 */
export const InputDropdown = ({
  label,
  name,
  options,
  disabled,
  onSelect,
  tip,
  sx,
  size = 'medium',
  renderValue,
  readOnly,
  displayEmpty,
  selectSx,
}: InputDropdownProps): ReactElement => (
  <Field id={`${name}-field`} name={name}>
    {({ field: { value }, form: { handleChange }, meta }: FieldAttributes<any>): ReactElement => {
      const hasError: boolean = meta.touched === true && meta.error !== undefined;
      const labelWithTip =
        label === undefined ? null : (
          <Box
            sx={{ opacity: disabled ? 0.5 : 1 }}
            display='flex'
            gap='0.25rem'
            alignItems='center'
          >
            {label}
            {tip !== undefined ? <InputTooltip tip={tip} /> : null}
          </Box>
        );

      return (
        <FormControl fullWidth error={hasError} sx={sx}>
          {label !== undefined && (
            <InputLabel sx={{ opacity: disabled ? 0.5 : 1 }} id={`${name}-label`} htmlFor={name}>
              {labelWithTip}
            </InputLabel>
          )}
          <Select
            size={size}
            labelId={label === undefined ? undefined : `${name}-label`}
            id={name}
            value={value ?? ''}
            name={name}
            renderValue={renderValue}
            label={label}
            readOnly={readOnly}
            disabled={disabled}
            displayEmpty={displayEmpty}
            onChange={async (e): Promise<void> => {
              await handleChange(e);

              await onSelect?.(e.target.value);
            }}
            // Close outlined box + fix height if label is undefined
            sx={{
              ...selectSx,
              ...(label === undefined
                ? {
                    fieldset: {
                      top: 0,
                    },
                    legend: {
                      display: 'none',
                    },
                  }
                : {}),
              ...(readOnly ? { pointerEvents: 'none' } : {}),
            }}
          >
            {options.map((opt): ReactElement => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText id={`${name}-helper-text`}>{hasError ? meta.error : ' '}</FormHelperText>
        </FormControl>
      );
    }}
  </Field>
);
